// useSheetVariant — picks the right BottomSheet variant for the current
// viewport (per W34.3.1 design decision + AGENTS.md §3 #11 modularity).
//
// "Sheet" in this hook's vocabulary means the BottomSheet surface, NOT the
// 撕信 container. Callers compose the variant with their own content +
// confirm/cancel UX. The destructive variant (whether 撕信 animation plays)
// is a content concern, not a variant concern.
//
// Viewport semantics (per DESIGN.md §4.3 + useIsMobile):
//   - mobile (< 768px): BottomSheet variant="mobile", 90vh, slide-up
//   - desktop (≥ 768px): BottomSheet variant="desktop-modal" — caller can
//     pass a custom width via `desktopWidth` (e.g. CompletionDialog wants
//     480px, ConfirmDialog wants 440px).

import { useIsMobile } from "./useIsMobile";
import type { BottomSheetVariant } from "@/components/patterns/BottomSheet";

export interface UseSheetVariantOptions {
  /**
   * Centered modal width on desktop. Defaults to 440px (ConfirmDialog
   * canonical). Pass 480 for CompletionDialog's slightly wider card, or
   * 520 to use BottomSheet's built-in default.
   */
  desktopWidth?: 440 | 480 | 520;
}

export interface SheetVariantHandle {
  /** The BottomSheet variant to pass to `<BottomSheet variant=...>`. */
  variant: BottomSheetVariant;
  /**
   * Centered modal width to apply on desktop. Mobile always renders
   * full-width per BottomSheet's `variant="mobile"` rules, so callers
   * should only set width when `!isMobile`.
   */
  desktopWidth: number;
  /** True when viewport is below the tablet breakpoint. */
  isMobile: boolean;
}

/**
 * useSheetVariant — selects BottomSheet variant + width by viewport.
 *
 * Per W34.3.1: callers do NOT hardcode viewports or breakpoints; they read
 * `isMobile` from this hook and pass the variant/width to BottomSheet.
 *
 * @example
 *   const { variant, desktopWidth, isMobile } = useSheetVariant({ desktopWidth: 440 });
 *   return (
 *     <BottomSheet variant={variant} className={!isMobile ? `w-[${desktopWidth}px]` : undefined} ...>
 *       {body}
 *     </BottomSheet>
 *   );
 */
export function useSheetVariant(
  options: UseSheetVariantOptions = {},
): SheetVariantHandle {
  const isMobile = useIsMobile();
  return {
    variant: isMobile ? "mobile" : "desktop-modal",
    desktopWidth: options.desktopWidth ?? 440,
    isMobile,
  };
}